/**
 * Panel de resumen agregado por estancia (con consolidación IA multi-vista).
 *
 * Por cada estancia:
 *  - Si existe consolidado IA → se muestra DESTACADO como valor principal (con
 *    razonamiento en tooltip). El promedio simple se muestra como "referencia".
 *  - Si NO existe pero hay ≥2 fotos con medición → botón "Refinar con IA" que
 *    dispara POST /siniestros/{id}/rooms/consolidate.
 *  - Si solo hay 1 foto → se muestra el valor tal cual (sin consolidación
 *    posible por definición).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Ruler, Home, RefreshCcw, Loader2, Sparkles, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function fmt(n) {
  if (n == null || typeof n !== "number") return "—";
  return n.toLocaleString("es-ES", { maximumFractionDigits: 2 });
}

export function RoomSummariesPanel({ siniestroId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refining, setRefining] = useState(null); // "roomId|category|unidad" que se está refinando
  const [consolidationEnabled, setConsolidationEnabled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sum, cfg] = await Promise.all([
        api.get(`/siniestros/${siniestroId}/room-summaries`),
        api.get(`/org/settings`).catch(() => ({ data: { settings: {} } })),
      ]);
      setItems(sum.data?.items || []);
      setConsolidationEnabled(!!cfg.data?.settings?.ai_consolidation_enabled);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [siniestroId, refreshKey]);

  const refine = async (roomId, category, unidad) => {
    const key = `${roomId}|${category}|${unidad}`;
    setRefining(key);
    try {
      await api.post(`/siniestros/${siniestroId}/rooms/consolidate`, {
        room_id: roomId,
        category,
        unidad,
      }, { timeout: 120_000 });
      toast.success("Dimensión refinada por IA");
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message;
      if (status === 400) {
        toast.error(detail || "Necesitas al menos 2 fotos de la estancia con medición.");
      } else if (status === 502) {
        toast.error("La IA no pudo consolidar. Revisa las fotos.");
      } else {
        toast.error("No se pudo refinar: " + detail);
      }
    } finally {
      setRefining(null);
    }
  };

  if (!loading && items.length === 0) return null;

  return (
    <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-4 mb-4" data-testid="room-summaries-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-[#D85A30]" />
          <h3 className="font-heading text-sm font-semibold text-[#2C2C2A]">
            Superficies por estancia
          </h3>
          <span className="text-[11px] text-[#7A786F]">
            (IA multi-vista sobre las fotos de cada estancia)
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-[11px] text-[#7A786F] hover:text-[#D85A30] flex items-center gap-1"
          title="Actualizar"
          data-testid="room-summaries-refresh"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <div
            key={r.room_id}
            className="border border-[#E5E2D8] rounded-[10px] p-3 bg-[#FBFAF6]"
            data-testid={`room-summary-${r.room_id}`}
          >
            <div className="text-[11px] uppercase tracking-wider text-[#7A2A14] font-semibold mb-2">
              {r.room_label}
            </div>
            <div className="space-y-3">
              {r.categories.map((c) => {
                const key = `${r.room_id}|${c.category}|${c.unidad}`;
                const isRefining = refining === key;
                const cons = c.consolidado;
                const canRefine = consolidationEnabled && c.count >= 2 && !cons;

                return (
                  <div key={c.category + c.unidad} className="text-xs">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Ruler className="h-3 w-3 text-[#D85A30] relative top-[1px]" />
                      <span className="text-[#7A786F]">{c.category_label}:</span>

                      {cons ? (
                        <>
                          <span
                            className="text-lg font-bold text-[#1D9E75]"
                            data-testid={`room-consolidated-${r.room_id}-${c.category}`}
                          >
                            {fmt(cons.valor_consolidado)}
                          </span>
                          <span className="text-[11px] text-[#7A2A14]">{cons.unidad || c.unidad}</span>
                          {cons.incertidumbre_pct != null && (
                            <span className="text-[10px] text-[#1D9E75] font-medium">
                              ±{cons.incertidumbre_pct}%
                            </span>
                          )}
                          <span
                            className="text-[9px] font-semibold text-[#1D6D57] bg-[#E4F5EE] px-1.5 py-0.5 rounded flex items-center gap-0.5"
                            title={cons.razonamiento || "Refinado por IA con análisis multi-vista"}
                          >
                            <Sparkles className="h-2 w-2" /> IA refinada
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-lg font-bold text-[#D85A30]">
                            {fmt(c.promedio)}
                          </span>
                          <span className="text-[11px] text-[#7A2A14]">{c.unidad}</span>
                          {c.incertidumbre_pct_promedio != null && (
                            <span className="text-[10px] text-[#B98A18] font-medium">
                              ±{fmt(c.incertidumbre_pct_promedio)}%
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <div className="text-[10px] text-[#7A786F] pl-4 mt-0.5">
                      {c.count > 1
                        ? `Estimaciones individuales: ${fmt(c.min)}–${fmt(c.max)} ${c.unidad} · ${c.count} fotos`
                        : "1 foto"}
                      {cons && ` · Promedio simple sería ${fmt(c.promedio)} ${c.unidad}`}
                    </div>

                    {cons && cons.razonamiento && (
                      <div className="text-[10px] text-[#7A786F] pl-4 mt-1 italic flex items-start gap-1">
                        <Info className="h-2.5 w-2.5 shrink-0 mt-0.5" />
                        <span>{cons.razonamiento}</span>
                      </div>
                    )}

                    {canRefine && (
                      <div className="mt-1.5 pl-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => refine(r.room_id, c.category, c.unidad)}
                          disabled={isRefining}
                          className="h-6 text-[10px] px-2 border-[#D85A30] text-[#D85A30] hover:bg-[#FAECE7]"
                          data-testid={`refine-btn-${r.room_id}-${c.category}`}
                        >
                          {isRefining ? (
                            <><Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" /> Refinando…</>
                          ) : (
                            <><Sparkles className="h-2.5 w-2.5 mr-1" /> Refinar con IA multi-vista</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#7A786F] italic mt-2">
        Las dimensiones marcadas &quot;IA refinada&quot; se calculan analizando TODAS las fotos de la estancia a la vez, triangulando perspectivas — son más precisas que el promedio simple. Se usan directamente en la valoración con IA.
      </p>
    </div>
  );
}
